import { useEffect, useState } from 'react'
import { ClipboardList, Star, Loader2, Link2, RotateCw, Trash2, Copy, Check, ExternalLink, Send, Smile, BarChart3, Image as ImageIcon, Download, Megaphone, Eye, EyeOff } from 'lucide-react'
import { api } from '../lib/api.js'
import { useConfirm } from '../components/ConfirmProvider.jsx'
import PrImageEditor from '../components/PrImageEditor.jsx'
import { PR_MAIN_SQ, PR_MAIN_A4, CST_PROMOTE_A4 } from '../lib/imageTemplates.js'

/**
 * Survey-related cards that share a single summary fetch. Renders ONE card
 * per slug in `cards`:
 *
 *   'report'      HR report public link + counts
 *   'pr-material' PR material editor (no fetch dependency, but kept here)
 *   'pr-banner'   Registration QR banner editor
 *   'cst'         CST card (link + mean + responses drill-in)
 *
 * Defaults to all four for backward compat. The Operation tabs each pass
 * the subset they care about so the same panel pieces live in their
 * conceptually-correct tabs.
 */
const ALL_CARDS = ['report', 'pr-material', 'pr-banner', 'cst-banner', 'cst']
export default function OperationSurveysPanel({ opId, op, cards = ALL_CARDS, layout = 'stack', refreshKey, onChange }) {
  const wanted = new Set(cards)
  const [data, setData]   = useState(null)
  const [error, setError] = useState(null)
  const [tab, setTab]     = useState('summary')

  function reload() {
    setError(null)
    api.operations.surveys.summary(opId).then(setData).catch((e) => setError(e.message))
  }

  // Refetch on opId change AND whenever the parent bumps refreshKey (e.g. a CST
  // response was hidden/deleted) so the mean/count card reflects the change.
  useEffect(() => { reload() /* eslint-disable-next-line */ }, [opId, refreshKey])

  if (error) return <Err msg={error} />
  if (!data) return <Loader />

  // Two layouts:
  //   stack    — single column with vertical spacing (default; used by
  //              CST scores + Information tabs where one card is the focus)
  //   masonry  — CSS multi-column packing: cards of varying heights stack
  //              like bricks, taller cards in one column while shorter ones
  //              fill the next. Used by PR media which has multiple
  //              image-preview cards of different heights.
  const wrapperCls = layout === 'masonry'
    ? 'columns-1 lg:columns-2 gap-4 [column-fill:_balance] [&>*]:break-inside-avoid [&>*]:mb-4'
    : 'space-y-4'

  // PR Material has two sub-slots (sq + a4) inside its own internal grid, so
  // it deserves the full row in masonry. Wrap it in a column-span-all div
  // (only relevant inside the multi-column wrapper; harmless in stack mode).
  const fullSpanCls = layout === 'masonry' ? '[column-span:all]' : ''

  return (
    <div className={wrapperCls}>
      {wanted.has('report')      && <ReportCard      opId={opId} info={data.report} cstCount={data.cst.count} regCount={data.registration.count} onChange={reload} />}
      {wanted.has('pr-material') && (
        <div className={fullSpanCls}>
          <PrMaterialCard op={op} />
        </div>
      )}
      {wanted.has('pr-banner')   && <PrBannerCard    opId={opId} op={op} registrationLink={data.registration.link} prImage={data.pr_image} onChange={reload} />}
      {wanted.has('cst-banner')  && <CstBannerCard   opId={opId} op={op} cstLink={data.cst.link} cstImage={data.cst_image} onChange={reload} />}
      {wanted.has('cst')         && <CstCard         opId={opId} info={data.cst} onChange={reload} onOpenResponses={() => setTab('cst-responses')} />}

      {wanted.has('cst') && tab === 'cst-responses' && (
        <CstResponsesPanel opId={opId} refreshKey={refreshKey}
          onClose={() => setTab('summary')}
          onChange={() => { reload(); onChange?.() }} />
      )}
    </div>
  )
}


/* ──────────────────────────────── PR Banner (registration QR) ───────────── */

function PrBannerCard({ opId, op, registrationLink, prImage, onChange }) {
  const confirm = useConfirm()
  const [editorOpen, setEditorOpen] = useState(false)
  const [imgUrl, setImgUrl] = useState(null)
  const [dlUrl,  setDlUrl]  = useState(null)
  const [busy, setBusy]     = useState(false)
  const hasImage = !!prImage?.exists

  // Lazy-load a fresh signed URL on mount (and after re-save). Presigned URLs
  // are valid for ~7 days; we re-fetch every panel mount to keep them fresh.
  useEffect(() => {
    if (!hasImage) { setImgUrl(null); setDlUrl(null); return }
    let cancelled = false
    api.operations.prImage.get(opId)
      .then((r) => {
        if (cancelled) return
        setImgUrl(r?.url || null)
        setDlUrl(r?.download_url || r?.url || null)
      })
      .catch(() => { if (!cancelled) { setImgUrl(null); setDlUrl(null) } })
    return () => { cancelled = true }
  }, [opId, hasImage, prImage?.updated_at])

  async function removeImg() {
    if (!await confirm({
      title: 'ลบภาพประชาสัมพันธ์',
      message: 'ภาพที่บันทึกไว้จะถูกลบออกจากเซิร์ฟเวอร์',
      confirmLabel: 'ลบ', tone: 'danger',
    })) return
    setBusy(true)
    try { await api.operations.prImage.remove(opId); setImgUrl(null); onChange() }
    finally   { setBusy(false) }
  }

  return (
    <Card title="ภาพประชาสัมพันธ์ (ลงทะเบียน)" icon={ImageIcon}
      subtitle="แบนเนอร์พร้อม QR ลิงก์ลงทะเบียน — สำหรับแชร์ในไลน์/Facebook">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[12.5px] text-zinc-500 leading-snug">
          {hasImage
            ? 'บันทึกไว้แล้ว — HR ดาวน์โหลดได้ที่หน้ารายงาน'
            : 'กดสร้างภาพเพื่อเปิดเครื่องมือออกแบบแบนเนอร์'}
        </p>
        <button onClick={() => setEditorOpen(true)} disabled={!registrationLink}
          title={!registrationLink ? 'ลิงก์ลงทะเบียนถูกยกเลิก — สร้างลิงก์ก่อนที่ Patient list tab' : undefined}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-50 disabled:cursor-not-allowed">
          <ImageIcon size={12} /> {hasImage ? 'แก้ไขภาพ' : 'สร้างภาพ'}
        </button>
      </div>

      {hasImage && (
        <div className="mt-3 rounded-lg ring-1 ring-amber-200/70 bg-amber-50/30 p-2">
          {imgUrl ? (
            <a href={dlUrl || imgUrl} className="block group relative">
              <img src={imgUrl} alt="PR flyer"
                className="block w-full max-w-[220px] mx-auto rounded ring-1 ring-zinc-200" />
              <span className="absolute inset-0 rounded bg-zinc-900/0 group-hover:bg-zinc-900/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
                <Download size={20} />
              </span>
            </a>
          ) : (
            <div className="text-[11.5px] text-zinc-400 text-center py-3 inline-flex items-center gap-1.5 w-full justify-center">
              <Loader2 size={11} className="animate-spin" /> โหลดภาพ…
            </div>
          )}
          <button onClick={removeImg} disabled={busy}
            className="mt-1.5 text-[11.5px] text-zinc-400 hover:text-rose-600 inline-flex items-center gap-1">
            <Trash2 size={11} /> ลบภาพที่บันทึกไว้
          </button>
        </div>
      )}

      {editorOpen && (
        <PrImageEditor
          opId={opId}
          companyId={op?.company_id}
          companyName={op?.company_name}
          startDate={op?.start_date}
          endDate={op?.end_date}
          registrationLink={registrationLink}
          saveImage={(dataUri) => api.operations.prImage.save(opId, dataUri)}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); onChange() }}
        />
      )}
    </Card>
  )
}


/* ──────────────────────────────── CST QR Poster ────────────────────────── */
/* Sister card to PrBannerCard but for the CST satisfaction survey. Uses the
   CST_PROMOTE_A4 template; the QR encodes the CST link (data.cst.link).
   Storage is a separate Wasabi key (cst_image_key), so this poster lives
   independently from the registration banner. NOT surfaced in HR portal. */

function CstBannerCard({ opId, op, cstLink, cstImage, onChange }) {
  const confirm = useConfirm()
  const [editorOpen, setEditorOpen] = useState(false)
  const [imgUrl, setImgUrl] = useState(null)
  const [dlUrl,  setDlUrl]  = useState(null)
  const [busy, setBusy]     = useState(false)
  const hasImage = !!cstImage?.exists

  useEffect(() => {
    if (!hasImage) { setImgUrl(null); setDlUrl(null); return }
    let cancelled = false
    api.operations.cstImage.get(opId)
      .then((r) => {
        if (cancelled) return
        setImgUrl(r?.url || null)
        setDlUrl(r?.download_url || r?.url || null)
      })
      .catch(() => { if (!cancelled) { setImgUrl(null); setDlUrl(null) } })
    return () => { cancelled = true }
  }, [opId, hasImage, cstImage?.updated_at])

  async function removeImg() {
    if (!await confirm({
      title: 'ลบป้าย QR CST',
      message: 'ภาพที่บันทึกไว้จะถูกลบออกจากเซิร์ฟเวอร์',
      confirmLabel: 'ลบ', tone: 'danger',
    })) return
    setBusy(true)
    try { await api.operations.cstImage.remove(opId); setImgUrl(null); onChange() }
    finally   { setBusy(false) }
  }

  return (
    <Card title="ป้าย QR แบบประเมินความพึงพอใจ" icon={ImageIcon}
      subtitle="โปสเตอร์ A4 พร้อม QR ลิงก์ CST — สำหรับติดในรถ/ส่งให้คนไข้สแกนหลังจบบริการ">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[12.5px] text-zinc-500 leading-snug">
          {hasImage
            ? 'บันทึกไว้แล้ว'
            : 'กดสร้างภาพเพื่อเปิดเครื่องมือออกแบบ — เลื่อน/ปรับขนาด QR ได้'}
        </p>
        <button onClick={() => setEditorOpen(true)} disabled={!cstLink}
          title={!cstLink ? 'ลิงก์ CST ยังไม่ได้สร้าง — ไปที่แท็บ CST scores เพื่อสร้างลิงก์ก่อน' : undefined}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-50 disabled:cursor-not-allowed">
          <ImageIcon size={12} /> {hasImage ? 'แก้ไขภาพ' : 'สร้างภาพ'}
        </button>
      </div>

      {hasImage && (
        <div className="mt-3 rounded-lg ring-1 ring-amber-200/70 bg-amber-50/30 p-2">
          {imgUrl ? (
            <a href={dlUrl || imgUrl} className="block group relative">
              <img src={imgUrl} alt="CST QR poster"
                className="block w-full max-w-[220px] mx-auto rounded ring-1 ring-zinc-200" />
              <span className="absolute inset-0 rounded bg-zinc-900/0 group-hover:bg-zinc-900/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
                <Download size={20} />
              </span>
            </a>
          ) : (
            <div className="text-[11.5px] text-zinc-400 text-center py-3 inline-flex items-center gap-1.5 w-full justify-center">
              <Loader2 size={11} className="animate-spin" /> โหลดภาพ…
            </div>
          )}
          <button onClick={removeImg} disabled={busy}
            className="mt-1.5 text-[11.5px] text-zinc-400 hover:text-rose-600 inline-flex items-center gap-1">
            <Trash2 size={11} /> ลบภาพที่บันทึกไว้
          </button>
        </div>
      )}

      {editorOpen && (
        <PrImageEditor
          opId={opId}
          companyId={op?.company_id}
          companyName={op?.company_name}
          startDate={op?.start_date}
          endDate={op?.end_date}
          template={CST_PROMOTE_A4}
          registrationLink={cstLink}                      /* prop name is generic — encodes the CST link here */
          saveImage={(dataUri) => api.operations.cstImage.save(opId, dataUri)}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); onChange() }}
        />
      )}
    </Card>
  )
}


/* ──────────────────────────────── PR Material ──────────────────────────── */

function PrMaterialCard({ op }) {
  return (
    <Card title="PR Material" icon={Megaphone}
      subtitle="ภาพประชาสัมพันธ์สำหรับ HR ใช้โปรโมต Tooth Taxi — 1:1 สำหรับโซเชียล / A4 สำหรับโปสเตอร์">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PrMaterialSlot template={PR_MAIN_SQ} op={op}
          fetchUrl={() => api.operations.prMainSq.get(op.id)}
          saveImage={(d) => api.operations.prMainSq.save(op.id, d)}
          remove={() => api.operations.prMainSq.remove(op.id)} />
        <PrMaterialSlot template={PR_MAIN_A4} op={op}
          fetchUrl={() => api.operations.prMainA4.get(op.id)}
          saveImage={(d) => api.operations.prMainA4.save(op.id, d)}
          remove={() => api.operations.prMainA4.remove(op.id)} />
      </div>
    </Card>
  )
}

function PrMaterialSlot({ template, op, fetchUrl, saveImage, remove }) {
  const confirm = useConfirm()
  const [editorOpen, setEditorOpen] = useState(false)
  const [imgUrl, setImgUrl] = useState(null)
  const [dlUrl,  setDlUrl]  = useState(null)
  const [busy, setBusy]     = useState(false)
  const [tick, bump]        = useState(0)

  useEffect(() => {
    let cancelled = false
    fetchUrl().then((r) => {
      if (cancelled) return
      setImgUrl(r?.url || null)
      setDlUrl(r?.download_url || r?.url || null)
    }).catch(() => { if (!cancelled) { setImgUrl(null); setDlUrl(null) } })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op?.id, tick])

  async function onSaved() { setEditorOpen(false); bump((n) => n + 1) }
  async function onRemove() {
    if (!await confirm({ title: 'ลบภาพประชาสัมพันธ์', message: 'ภาพที่บันทึกไว้จะถูกลบออกจากเซิร์ฟเวอร์', confirmLabel: 'ลบ', tone: 'danger' })) return
    setBusy(true)
    try { await remove(); bump((n) => n + 1) } finally { setBusy(false) }
  }

  const isSquare = template.key === 'pr-main-sq'

  return (
    <div className="rounded-xl ring-1 ring-amber-200/60 bg-gradient-to-br from-amber-50/30 to-orange-50/10 p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700/80 font-semibold">{isSquare ? 'Square 1:1' : 'A4 portrait'}</div>
          <div className="text-[13px] font-semibold text-zinc-900">{isSquare ? 'ภาพโซเชียล' : 'โปสเตอร์ A4'}</div>
        </div>
        <button onClick={() => setEditorOpen(true)}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-medium">
          {imgUrl ? 'แก้ไข' : 'สร้าง'}
        </button>
      </div>
      <div className="rounded-lg bg-white ring-1 ring-zinc-200/70 overflow-hidden">
        {imgUrl ? (
          // Click → download (presigned URL has Content-Disposition: attachment).
          <a href={dlUrl || imgUrl} className="block group relative">
            <img src={imgUrl} alt={template.label}
              className="block w-full h-auto"
              style={{ aspectRatio: `${template.width}/${template.height}` }} />
            <span className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100 text-[11.5px] font-medium">
              คลิกเพื่อดาวน์โหลด
            </span>
          </a>
        ) : (
          <div className="flex items-center justify-center text-[12px] text-zinc-400 py-6"
            style={{ aspectRatio: `${template.width}/${template.height}` }}>
            ยังไม่มีภาพ
          </div>
        )}
      </div>
      {imgUrl && (
        <button onClick={onRemove} disabled={busy}
          className="mt-2 text-[11.5px] text-zinc-400 hover:text-rose-600 inline-flex items-center gap-1">
          <Trash2 size={11} /> ลบภาพ
        </button>
      )}
      {editorOpen && (
        <PrImageEditor
          template={template}
          opId={op.id}
          companyId={op.company_id}
          companyName={op.company_name}
          startDate={op.start_date}
          endDate={op.end_date}
          saveImage={saveImage}
          onClose={() => setEditorOpen(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  )
}


/* ──────────────────────────────── Registration ──────────────────────────── */

function RegistrationCard({ opId, op, info, prImage, onChange, onOpenResponses }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [imgUrl, setImgUrl] = useState(null)   // Wasabi presigned URL (lazy-loaded)
  const [dlUrl,  setDlUrl]  = useState(null)
  async function gen()    { setBusy(true); try { await api.operations.surveys.generateRegistration(opId); onChange() } finally { setBusy(false) } }
  async function revoke() {
    if (!await confirm({ title: 'ยกเลิกลิงก์ลงทะเบียน', message: 'ลิงก์ปัจจุบันจะใช้ไม่ได้อีก — คนที่ได้ลิงก์ไปแล้วจะเปิดไม่ได้', confirmLabel: 'ยกเลิกลิงก์', tone: 'warn' })) return
    setBusy(true); try { await api.operations.surveys.revokeRegistration(opId); onChange() } finally { setBusy(false) }
  }
  async function removeImg() {
    if (!await confirm({ title: 'ลบภาพประชาสัมพันธ์', message: 'ภาพที่บันทึกไว้จะถูกลบออกจากเซิร์ฟเวอร์', confirmLabel: 'ลบ', tone: 'danger' })) return
    setBusy(true); try { await api.operations.prImage.remove(opId); setImgUrl(null); onChange() } finally { setBusy(false) }
  }

  const hasImage = !!prImage?.exists

  // Lazy-load a fresh signed URL on mount (and after re-save). The signed URL
  // is valid for ~7 days; we re-request on every panel open to keep it fresh.
  useEffect(() => {
    if (!hasImage) { setImgUrl(null); setDlUrl(null); return }
    let cancelled = false
    api.operations.prImage.get(opId)
      .then((r) => {
        if (cancelled) return
        setImgUrl(r?.url || null)
        setDlUrl(r?.download_url || r?.url || null)
      })
      .catch(() => { if (!cancelled) { setImgUrl(null); setDlUrl(null) } })
    return () => { cancelled = true }
  }, [opId, hasImage, prImage?.updated_at])

  return (
    <Card title="Service Registration" icon={ClipboardList} subtitle="HR ส่งให้พนักงานลงทะเบียนใช้บริการ">
      <div className="flex items-baseline gap-2">
        <span className="text-[28px] font-bold text-zinc-900 tabular-nums">{info.count}</span>
        <span className="text-[12.5px] text-zinc-500">คน ลงทะเบียนแล้ว</span>
      </div>
      <LinkRow link={info.link} busy={busy} onGenerate={gen} onRevoke={revoke} />

      {info.link && (
        <div className="mt-3 pt-3 border-t border-zinc-100">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-zinc-700">
                <ImageIcon size={12} className="text-amber-500" /> ภาพประชาสัมพันธ์
              </div>
              <p className="text-[11.5px] text-zinc-500 mt-0.5">
                {hasImage ? `บันทึกไว้แล้ว — HR จะดาวน์โหลดได้ที่หน้ารายงานด้วย` : 'สร้างภาพแบนเนอร์พร้อม QR เพื่อแชร์ในไลน์/Facebook'}
              </p>
            </div>
            <button onClick={() => setEditorOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium">
              <ImageIcon size={12} /> {hasImage ? 'แก้ไขภาพ' : 'สร้างภาพ'}
            </button>
          </div>

          {hasImage && (
            <div className="mt-2.5 rounded-lg ring-1 ring-amber-200/70 bg-amber-50/30 p-2">
              {imgUrl ? (
                // Click → download (presigned URL carries Content-Disposition: attachment).
                <a href={dlUrl || imgUrl} className="block group relative">
                  <img src={imgUrl} alt="PR flyer"
                    className="block w-full max-w-[220px] mx-auto rounded ring-1 ring-zinc-200" />
                  <span className="absolute inset-0 rounded bg-zinc-900/0 group-hover:bg-zinc-900/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
                    <Download size={20} />
                  </span>
                </a>
              ) : (
                <div className="text-[11.5px] text-zinc-400 text-center py-3 inline-flex items-center gap-1.5 w-full justify-center">
                  <Loader2 size={11} className="animate-spin" /> โหลดภาพ…
                </div>
              )}
              <button onClick={removeImg} disabled={busy}
                className="mt-1.5 text-[11.5px] text-zinc-400 hover:text-rose-600 inline-flex items-center gap-1">
                <Trash2 size={11} /> ลบภาพที่บันทึกไว้
              </button>
            </div>
          )}
        </div>
      )}

      {info.count > 0 && (
        <button onClick={onOpenResponses}
          className="mt-2 text-[12.5px] text-amber-700 hover:text-amber-800 inline-flex items-center gap-1">
          <Send size={12} /> ดูรายชื่อทั้งหมด
        </button>
      )}

      {editorOpen && (
        <PrImageEditor
          opId={opId}
          companyId={op?.company_id}
          companyName={op?.company_name}
          startDate={op?.start_date}
          endDate={op?.end_date}
          registrationLink={info.link}
          saveImage={(dataUri) => api.operations.prImage.save(opId, dataUri)}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); onChange() }}
        />
      )}
    </Card>
  )
}

function RegistrationResponsesPanel({ opId, onClose }) {
  const [rows, setRows] = useState(null)
  useEffect(() => { api.operations.surveys.registrations(opId).then(setRows) }, [opId])
  return (
    <Card title="รายชื่อผู้ลงทะเบียน" subtitle="เรียงตามเวลาที่ส่งล่าสุดอยู่บนสุด" right={<button onClick={onClose} className="text-[12px] text-zinc-500 hover:text-zinc-900">ปิด</button>}>
      {!rows ? <Loader /> : rows.length === 0 ? <Empty msg="ยังไม่มีผู้ลงทะเบียน" /> : (
        <div className="overflow-x-auto -mx-3 md:-mx-5">
          <table className="w-full text-[12.5px]">
            <thead className="bg-zinc-50/70 text-[10.5px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 md:px-5 py-2 text-left">เวลา</th>
                <th className="px-3 md:px-5 py-2 text-left">ชื่อ-สกุล</th>
                <th className="px-3 md:px-5 py-2 text-left">โทร</th>
                <th className="px-3 md:px-5 py-2 text-left">บัตร ปชช.</th>
                <th className="px-3 md:px-5 py-2 text-left">ช่วงเวลาที่เลือก</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100 last:border-b-0">
                  <td className="px-3 md:px-5 py-1.5 text-zinc-500 tabular-nums">{fmtFull(r.created_at)}</td>
                  <td className="px-3 md:px-5 py-1.5 text-zinc-900">{r.fullname}</td>
                  <td className="px-3 md:px-5 py-1.5 text-zinc-700 tabular-nums">{r.phone}</td>
                  <td className="px-3 md:px-5 py-1.5 text-zinc-500 tabular-nums">{r.idcard}</td>
                  <td className="px-3 md:px-5 py-1.5 text-zinc-700">{slotLabel(r.time_slot)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}


/* ──────────────────────────────── CST ──────────────────────────────────── */

function CstCard({ opId, info, onChange, onOpenResponses }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  async function gen()    { setBusy(true); try { await api.operations.surveys.generateCst(opId); onChange() } finally { setBusy(false) } }
  async function revoke() {
    if (!await confirm({ title: 'ยกเลิกลิงก์ CST', message: 'ลิงก์ปัจจุบันจะใช้ไม่ได้อีก — คนที่ได้ลิงก์ไปแล้วจะส่งคะแนนไม่ได้', confirmLabel: 'ยกเลิกลิงก์', tone: 'warn' })) return
    setBusy(true); try { await api.operations.surveys.revokeCst(opId); onChange() } finally { setBusy(false) }
  }

  const mean = info.mean != null ? Number(info.mean) : null

  return (
    <Card title="Customer Service Score (CST)" icon={Star} subtitle="แบบประเมินความพึงพอใจ 6 ข้อ คะแนน 1–5">
      <div className="flex items-baseline gap-3">
        {mean == null ? (
          <span className="text-[14px] text-zinc-400 italic">ยังไม่มีคะแนน</span>
        ) : (
          <>
            <span className={'text-[28px] font-bold tabular-nums ' + meanColor(mean)}>{mean.toFixed(2)}</span>
            <span className="text-[12.5px] text-zinc-500">เฉลี่ย ({info.count} responses)</span>
          </>
        )}
      </div>
      <LinkRow link={info.link} busy={busy} onGenerate={gen} onRevoke={revoke} />
      {info.count > 0 && (
        <button onClick={onOpenResponses}
          className="mt-2 text-[12.5px] text-amber-700 hover:text-amber-800 inline-flex items-center gap-1">
          <Smile size={12} /> ดูคำตอบทั้งหมด
        </button>
      )}
    </Card>
  )
}

function CstResponsesPanel({ opId, onClose, onChange, refreshKey }) {
  const [data, setData] = useState(null)
  // Refetch on opId change and whenever refreshKey bumps (a hide/delete here or
  // on the main CST page propagates back through the parent → fresh data).
  useEffect(() => {
    let cancelled = false
    api.operations.surveys.cstResponses(opId).then((d) => { if (!cancelled) setData(d) })
    return () => { cancelled = true }
  }, [opId, refreshKey])

  const hiddenCount = data?.rows ? data.rows.filter((r) => r.hidden).length : 0

  return (
    <Card title="ผลประเมินรายราย"
      subtitle={data?.mean != null ? `ค่าเฉลี่ยรวม ${Number(data.mean).toFixed(2)} จาก ${data.count} responses` : undefined}
      right={<button onClick={onClose} className="text-[12px] text-zinc-500 hover:text-zinc-900">ปิด</button>}>
      {!data ? <Loader /> : data.rows.length === 0 ? <Empty msg="ยังไม่มีคำตอบ" /> : (
        <>
          <p className="text-[11.5px] text-zinc-400 mb-2 leading-snug">
            <EyeOff size={10} className="inline -mt-0.5 mr-0.5" /> ซ่อน = ตัด response นี้ออกจากรายงานของ HR ทั้งหมด (ทั้งคะแนนและข้อความ) แต่ยังเห็นในหน้านี้ ·
            <Trash2 size={10} className="inline -mt-0.5 mx-0.5 text-rose-400" /> ลบ = ลบถาวรทุกที่ ย้อนกลับไม่ได้
            {hiddenCount > 0 && <span className="ml-1 text-zinc-500">· ซ่อนจาก HR อยู่ {hiddenCount}</span>}
          </p>
          <ul className="space-y-2">
            {data.rows.map((r) => (
              <CstResponseRow key={r.id} r={r} opId={opId} onChange={onChange} />
            ))}
          </ul>
        </>
      )}
    </Card>
  )
}

function CstResponseRow({ r, opId, onChange }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const [confirming, setConfirming] = useState(false)
  const hidden = !!r.hidden
  const m = (r.q1 + r.q2 + r.q3 + r.q4 + r.q5 + r.q6) / 6

  async function toggleHide() {
    setBusy(true); setErr(null)
    try { await api.operations.surveys.updateCstResponse(opId, r.id, { hidden: !hidden }); onChange?.() }
    catch (e) { setErr(e?.message || 'error'); setBusy(false) }
  }
  async function remove() {
    setBusy(true); setErr(null)
    try { await api.operations.surveys.deleteCstResponse(opId, r.id); onChange?.() }
    catch (e) { setErr(e?.message || 'error'); setBusy(false); setConfirming(false) }
  }

  return (
    <li className={'rounded-lg ring-1 px-3 py-2 transition ' +
      (hidden ? 'ring-zinc-200 bg-zinc-100/60 opacity-70' : 'ring-zinc-200/70 bg-white')}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[11.5px] uppercase tracking-wider text-zinc-400 tabular-nums">{fmtFull(r.created_at)}</span>
          {hidden && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-zinc-200 text-zinc-600 inline-flex items-center gap-1 shrink-0">
              <EyeOff size={9} /> ซ่อนจาก HR
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={'tabular-nums font-semibold text-[13px] ' + meanColor(m)}>{m.toFixed(2)}</span>
          {confirming ? (
            <span className="inline-flex items-center gap-1">
              <button onClick={remove} disabled={busy}
                className="px-2 h-7 rounded-md text-[11px] font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1 whitespace-nowrap">
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} ลบถาวร
              </button>
              <button onClick={() => setConfirming(false)} disabled={busy}
                className="px-2 h-7 rounded-md text-[11px] text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-100 disabled:opacity-50">
                ยกเลิก
              </button>
            </span>
          ) : (
            <>
              <button onClick={toggleHide} disabled={busy} title={hidden ? 'แสดงให้ HR เห็น' : 'ซ่อนจาก HR'}
                className={'w-7 h-7 rounded-md inline-flex items-center justify-center transition disabled:opacity-50 ' +
                  (hidden
                    ? 'text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50'
                    : 'text-zinc-400 hover:text-amber-700 hover:bg-amber-50')}>
                {busy ? <Loader2 size={11} className="animate-spin" /> : hidden ? <Eye size={13} /> : <EyeOff size={13} />}
              </button>
              <button onClick={() => setConfirming(true)} disabled={busy} title="ลบ response นี้ถาวร"
                className="w-7 h-7 rounded-md inline-flex items-center justify-center text-zinc-400 hover:text-rose-700 hover:bg-rose-50 transition disabled:opacity-50">
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="mt-1 flex items-center gap-1">
        {[r.q1, r.q2, r.q3, r.q4, r.q5, r.q6].map((q, i) => (
          <span key={i} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] tabular-nums bg-zinc-50 ring-1 ring-zinc-200 text-zinc-700">
            Q{i+1} <span className={meanColor(q)}>{q}</span>
          </span>
        ))}
      </div>
      {r.suggestion && (
        <p className={'mt-1.5 text-[12.5px] italic ' + (hidden ? 'text-zinc-500 line-through decoration-zinc-300' : 'text-zinc-700')}>"{r.suggestion}"</p>
      )}
      {err && (
        <div className="mt-1 text-[11px] text-rose-600">{err}</div>
      )}
    </li>
  )
}


/* ──────────────────────────────── HR Report ───────────────────────────── */

function ReportCard({ opId, info, cstCount, regCount, onChange }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)
  async function gen()    { setBusy(true); try { await api.operations.surveys.generateReport(opId); onChange() } finally { setBusy(false) } }
  async function revoke() {
    if (!await confirm({ title: 'ยกเลิกลิงก์รายงาน HR', message: 'ลิงก์รายงานปัจจุบันจะใช้ไม่ได้อีก — HR ที่ได้ลิงก์ไปแล้วจะเปิดไม่ได้', confirmLabel: 'ยกเลิกลิงก์', tone: 'warn' })) return
    setBusy(true); try { await api.operations.surveys.revokeReport(opId); onChange() } finally { setBusy(false) }
  }

  return (
    <Card title="HR Report" icon={BarChart3}
      subtitle="ลิงก์รายงานสรุป — HR เปิดเพื่อดูคะแนน CST, ความคิดเห็น และจำนวนผู้ลงทะเบียน (ไม่เผยข้อมูลส่วนตัว)">
      <div className="text-[12.5px] text-zinc-600">
        ข้อมูลที่แสดง: {regCount.toLocaleString()} ผู้ลงทะเบียน · {cstCount.toLocaleString()} CST responses
      </div>
      <LinkRow link={info.link} busy={busy} onGenerate={gen} onRevoke={revoke}
        generateLabel="สร้างลิงก์รายงาน HR" />
    </Card>
  )
}


/* ──────────────────────────────── shared ──────────────────────────────── */

function LinkRow({ link, busy, onGenerate, onRevoke, generateLabel = 'สร้างลิงก์สาธารณะ' }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    if (!link) return
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch {}
  }
  if (!link) {
    return (
      <button onClick={onGenerate} disabled={busy}
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium disabled:opacity-60">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} {generateLabel}
      </button>
    )
  }
  return (
    <div className="mt-3">
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-50 ring-1 ring-zinc-200 rounded-md text-[12px] text-zinc-700">
        <Link2 size={12} className="text-zinc-400 shrink-0" />
        <span className="truncate flex-1" title={link}>{link}</span>
        <button onClick={copy} className="px-1.5 py-0.5 rounded text-zinc-500 hover:text-amber-700 inline-flex items-center gap-1 text-[11px]">
          {copied ? <><Check size={11} className="text-emerald-600" /> คัดลอกแล้ว</> : <><Copy size={11} /> คัดลอก</>}
        </button>
        <a href={link} target="_blank" rel="noreferrer" className="px-1.5 py-0.5 rounded text-zinc-500 hover:text-amber-700 inline-flex items-center gap-1 text-[11px]">
          <ExternalLink size={11} /> เปิด
        </a>
      </div>
      <div className="mt-1.5 flex items-center gap-3 text-[11.5px]">
        <button onClick={onGenerate} disabled={busy} className="text-zinc-500 hover:text-amber-700 inline-flex items-center gap-1">
          <RotateCw size={11} /> สร้างลิงก์ใหม่
        </button>
        <button onClick={onRevoke} disabled={busy} className="text-zinc-400 hover:text-rose-600 inline-flex items-center gap-1">
          <Trash2 size={11} /> ยกเลิกลิงก์
        </button>
      </div>
    </div>
  )
}

function Card({ title, subtitle, icon: Icon, right, children }) {
  return (
    <div className="bg-white rounded-2xl ring-1 ring-zinc-200/80 p-5 md:p-6">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-[15px] font-semibold text-zinc-900 inline-flex items-center gap-1.5">
            {Icon && <Icon size={15} className="text-amber-500" />} {title}
          </h2>
          {subtitle && <p className="text-[12px] text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  )
}

function meanColor(v) {
  if (v == null) return 'text-zinc-500'
  if (v < 3) return 'text-rose-600'
  if (v < 4) return 'text-amber-600'
  return 'text-emerald-600'
}

function Loader() {
  return <div className="text-center py-8 text-zinc-400"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Loading…</div>
}
function Err({ msg }) { return <div className="px-3 py-2 rounded bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[13px]">{msg}</div> }
function Empty({ msg }) { return <div className="py-6 text-center text-zinc-400 text-[12.5px]">{msg}</div> }

function slotLabel(value) {
  if (!value || !value.includes('|')) return value || '—'
  const [a, b] = value.split('|')
  try {
    const da = new Date(a); const db = new Date(b)
    return `${da.toLocaleString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} – ${db.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })}`
  } catch {
    return value
  }
}
function fmtFull(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short' }) +
    ' · ' + d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}
